import { memo, useEffect, useMemo, useState } from 'react';
import {
    Checkbox,
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
    Spinner
} from '@chakra-ui/react';
import Swal from 'sweetalert2';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { SingleValue } from 'react-select';
import { twMerge } from 'tailwind-merge';
import { useDebounce } from 'usehooks-ts';

import { EmployeeInformation, UserCompany } from 'models/user/UserCompany';
import { Button, Creatable, Input, Label } from 'common/materials';
import { Employee, EmployeeBranch, EmployeePrefix, F1PositionMaster } from 'models/user/Employee';
import { ResponseList } from 'models';
import { CompanyPlatformMapping } from 'data/company/schemas';
import {
    useCheckUserIdIfExists,
    useDeleteUser,
    useEmployeeInformationByEmployeeCode,
    useEmployeeInformationByUserId,
    useF1PositionMaster
} from '../queries';

type FieldConfig = {
    asterisk?: boolean;
    required?: boolean;
    type: 'text' | 'select';
    name: keyof EmployeeInformation;
    existingName: keyof Employee;
    label: string;
    className: string;
};

const FIELDS: FieldConfig[] = [
    {
        asterisk: true,
        type: 'text',
        name: 'employee_code',
        existingName: 'EmployeeCode',
        label: 'Employee Code',
        className: 'col-span-6'
    },
    {
        required: true,
        asterisk: true,
        type: 'text',
        name: 'user_id',
        existingName: 'Username',
        label: 'User ID',
        className: 'col-span-6'
    },
    {
        asterisk: true,
        type: 'select',
        name: 'prefix',
        existingName: 'NamePrefix',
        label: 'Prefix',
        className: 'col-span-2'
    },
    {
        asterisk: true,
        type: 'text',
        existingName: 'FirstName_TH',
        name: 'first_name_th',
        label: 'First Name (TH)',
        className: 'col-span-4'
    },
    {
        asterisk: true,
        type: 'text',
        name: 'last_name_th',
        existingName: 'LastName_TH',
        label: 'Last Name (TH)',
        className: 'col-span-6'
    },
    {
        asterisk: true,
        type: 'text',
        name: 'first_name_en',
        existingName: 'FirstName',
        label: 'First Name (EN)',
        className: 'col-start-3 col-span-4'
    },
    {
        asterisk: true,
        type: 'text',
        name: 'last_name_en',
        existingName: 'LastName',
        label: 'Last Name',
        className: 'col-span-6'
    },
    {
        required: true,
        asterisk: true,
        type: 'text',
        name: 'email',
        existingName: 'Email',
        label: 'Email',
        className: 'col-span-6'
    },
    {
        type: 'select',
        name: 'branch',
        existingName: 'BranchCode',
        label: 'Branch',
        className: 'col-span-6'
    },
    {
        type: 'text',
        name: 'department',
        existingName: 'Department',
        label: 'Department',
        className: 'col-span-6'
    },
    {
        type: 'select',
        name: 'position',
        existingName: 'PositionCode',
        label: 'Position',
        className: 'col-span-6'
    }
];

const getBranchOptionLabel = (option: EmployeeBranch) => {
    if (!option.BRANCHCODE && !option.BRANCHNAME_TH) {
        return '';
    }

    return `${option.BRANCHCODE} - ${option.BRANCHNAME_TH}`;
};

const getPositionOptionLabel = (option: F1PositionMaster) => {
    if (!option.PositionCode && !option.PositionName) {
        return '';
    }

    return `${option.PositionCode} - ${option.PositionName} (${option.Description})`;
};

type Props = {
    isOpen: boolean;
    user?: UserCompany;
    onClose: () => void;
    onConfirmApprove: (
        user: UserCompany,
        status: 'approve' | 'active' | 'rejected' | 'inactive',
        approveWorkflow?: boolean,
        approveDms?: boolean
    ) => void | Promise<void>;
};

function ConfirmApproveUserModal(props: Props) {
    const { isOpen, user, onClose, onConfirmApprove } = props;

    const [invalidFields, setInvalidFields] = useState<string[]>([]);
    const [editedEmployeeInformation, setEditedEmployeeInformation] =
        useState<EmployeeInformation>();
    const [approveWorkflow, setApproveWorkflow] = useState(true);
    const [approveDms, setApproveDms] = useState(true);

    const { data: platform } = useQuery({
        enabled: !!user,
        queryKey: ['company-platform', { sid: user?.sid }],
        queryFn: ({ signal }) =>
            axios.get<ResponseList<CompanyPlatformMapping>>('/v1/company/platform_mapping', {
                signal,
                params: {
                    sid: user!.sid
                }
            }),
        select: response => response.data.result_list?.[0]
    });

    const { data: selectablePrefixes = [] } = useQuery({
        enabled: !!user && !!platform,
        queryKey: ['employee-prefix', { sid: user?.sid, platform_id: platform?.platform_id }],
        queryFn: ({ signal }) =>
            axios.get<ResponseList<EmployeePrefix>>('/v1/f1/employee-prefix', {
                signal,
                params: {
                    sid: user!.sid,
                    platform_id: platform!.platform_id
                }
            }),
        select: response => response.data.result_list,
        retry: 1
    });

    const { data: selectableBranches = [] } = useQuery({
        enabled: !!user && !!platform,
        queryKey: ['employee-branch', { sid: user?.sid, platform_id: platform?.platform_id }],
        queryFn: ({ signal }) =>
            axios.get<ResponseList<EmployeeBranch>>('/v1/f1/employee-branch', {
                signal,
                params: {
                    sid: user!.sid,
                    platform_id: platform!.platform_id
                }
            }),
        select: response => response.data.result_list,
        retry: 1
    });

    const { data: selectablePositions = [] } = useF1PositionMaster({
        enabled: !!user && !!platform,
        sid: user?.sid,
        platformId: platform?.platform_id
    });

    const debouncedEditedEmployeeCode = useDebounce(editedEmployeeInformation?.employee_code, 500);
    const debouncedEditedUserId = useDebounce(editedEmployeeInformation?.user_id, 500);

    const {
        isSuccess: userIdOk,
        isFetching: isFetchingCanUseUserId,
        refetch: refetchCanUseUserId
    } = useCheckUserIdIfExists({
        enabled: !!user && !!debouncedEditedUserId,
        sid: user?.sid,
        userId: debouncedEditedUserId,
        systemType: user?.system_type
    });

    const {
        data: existingEmployeeInformationByEmployeeCode,
        isFetching: isFetchingExistingEmployeeInformationByEmployeeCode,
        refetch: refetchExistingEmployeeInformationByEmployeeCode
    } = useEmployeeInformationByEmployeeCode({
        enabled: !!user?.sid && !!platform?.platform_id && !!debouncedEditedEmployeeCode,
        sid: user?.sid,
        platformId: platform?.platform_id,
        employeeCode: debouncedEditedEmployeeCode
    });

    const {
        data: existingEmployeeInformationByUserId,
        isFetching: isFetchingExistingEmployeeInformationByUserId,
        refetch: refetchExistingEmployeeInformationByUserId
    } = useEmployeeInformationByUserId({
        enabled: !!user?.sid && !!platform?.platform_id && !!debouncedEditedUserId,
        sid: user?.sid,
        platformId: platform?.platform_id,
        userId: debouncedEditedUserId
    });

    const validationResult = useMemo(() => {
        if (!userIdOk) {
            return {
                canApprove: false,
                message: `User ID "${debouncedEditedUserId}" ไม่สามารถใช้งานได้เนื่องจากมีอยู่แล้วในระบบ ต้องการลบออกหรือไม่?`,
                showRemoveUser: true
            };
        }

        if (!debouncedEditedEmployeeCode) {
            return {
                canApprove: true,
                existingEmployeeInformation: existingEmployeeInformationByUserId
            };
        }

        if (
            !existingEmployeeInformationByEmployeeCode &&
            existingEmployeeInformationByUserId &&
            existingEmployeeInformationByUserId.EmployeeCode !== debouncedEditedEmployeeCode
        ) {
            return {
                canApprove: false,
                message: `พบการลงทะเบียนรหัสพนักงาน "${existingEmployeeInformationByUserId?.EmployeeCode}" กับผู้ใช้งาน "${existingEmployeeInformationByUserId?.Username}" กรุณาตรวจสอบ Employee Master`,
                existingEmployeeInformation: existingEmployeeInformationByUserId
            };
        }

        if (
            existingEmployeeInformationByEmployeeCode &&
            !existingEmployeeInformationByEmployeeCode.Username
        ) {
            return {
                canApprove: true,
                existingEmployeeInformation: existingEmployeeInformationByEmployeeCode
            };
        }

        if (
            existingEmployeeInformationByEmployeeCode &&
            existingEmployeeInformationByEmployeeCode.Username !== debouncedEditedUserId
        ) {
            return {
                canApprove: false,
                message: `รหัสพนักงาน "${existingEmployeeInformationByEmployeeCode?.EmployeeCode}" ได้มีการกำหนดให้กับผู้ใช้งาน "${existingEmployeeInformationByEmployeeCode?.Username}" บนระบบเรียบร้อยแล้ว กรุณาระบุรหัสพนักงานใหม่อีกครั้ง`,
                existingEmployeeInformation: existingEmployeeInformationByEmployeeCode
            };
        }

        return {
            canApprove: true
        };
    }, [
        debouncedEditedEmployeeCode,
        debouncedEditedUserId,
        userIdOk,
        existingEmployeeInformationByUserId,
        existingEmployeeInformationByEmployeeCode
    ]);

    const { mutate: deleteUser, isPending: isPendingDeleteUser } = useDeleteUser();

    const selectedExistingPrefix = useMemo(
        () =>
            selectablePrefixes.find(prefix => {
                const search = validationResult.existingEmployeeInformation?.NamePrefix;
                return prefix.TitleCode === search || prefix.TitleName === search;
            }),
        [selectablePrefixes, validationResult.existingEmployeeInformation?.NamePrefix]
    );
    const selectedPrefix = useMemo(
        () =>
            selectablePrefixes.find(prefix => {
                const search = editedEmployeeInformation?.prefix;
                return prefix.TitleCode === search || prefix.TitleName === search;
            }),
        [selectablePrefixes, editedEmployeeInformation?.prefix]
    );

    const selectedExistingBranch = useMemo(
        () =>
            selectableBranches.find(branch => {
                const search = validationResult.existingEmployeeInformation?.BranchCode;
                return (
                    branch.BRANCHCODE === search ||
                    branch.BRANCHID === search ||
                    // branch.BRANCHNAME_EN === search ||
                    branch.BRANCHNAME_TH === search
                );
            }),
        [selectableBranches, validationResult.existingEmployeeInformation?.BranchCode]
    );
    const selectedBranch = useMemo(
        () =>
            selectableBranches.find(branch => {
                const search = editedEmployeeInformation?.branch;
                return (
                    branch.BRANCHCODE === search ||
                    branch.BRANCHID === search ||
                    // branch.BRANCHNAME_EN === search ||
                    branch.BRANCHNAME_TH === search
                );
            }),
        [selectableBranches, editedEmployeeInformation?.branch]
    );

    const selectedExistingPosition = useMemo(
        () =>
            selectablePositions.find(position => {
                const search = validationResult.existingEmployeeInformation?.PositionCode;
                return position.PositionCode === search || position.PositionName === search;
            }),
        [selectablePositions, validationResult.existingEmployeeInformation?.PositionCode]
    );
    const selectedPosition = useMemo(
        () =>
            selectablePositions.find(position => {
                const search = editedEmployeeInformation?.position;
                return position.PositionCode === search || position.PositionName === search;
            }),
        [selectablePositions, editedEmployeeInformation?.position]
    );

    useEffect(() => {
        if (!isOpen) {
            // reset
            setInvalidFields([]);
            setEditedEmployeeInformation(undefined);
        } else {
            setApproveWorkflow(true);
            setApproveDms(true);
        }
    }, [isOpen]);

    useEffect(() => {
        if (!user) {
            return;
        }

        // fallback for an edge case when `employee_information` is null
        if (!user.employee_information) {
            setEditedEmployeeInformation({
                branch: '',
                department: '',
                email: user.email || '',
                employee_code: user.f1_employee_code,
                first_name_en: user.f1_first_name || user.first_name,
                first_name_th: user.f1_first_name_th || user.th_first_name,
                last_name_en: user.f1_last_name || user.last_name,
                last_name_th: user.f1_last_name_th || user.th_last_name,
                position: '',
                prefix: '',
                user_id: user.f1_user_id || user.citizen_id.split('@')[0]
            });

            return;
        }

        // a new user will already have all requirement fields
        setEditedEmployeeInformation({ ...user.employee_information });
    }, [user]);

    const handleChangeEmployeeInformation = (key: string, value: string) => {
        setEditedEmployeeInformation(employeeInformation => {
            const updated = {
                ...employeeInformation,
                [key]: value
            } as EmployeeInformation;

            // Remove field from invalidFields if corrected
            if (value.trim()) {
                setInvalidFields(fields => fields.filter(fieldName => fieldName !== key));
            }

            return updated;
        });
    };

    const handlePrefixChange = (prefix: SingleValue<EmployeePrefix>) => {
        if (!prefix) {
            return;
        }

        setEditedEmployeeInformation(prevState =>
            prevState
                ? {
                      ...prevState,
                      prefix: prefix.TitleCode
                  }
                : undefined
        );
    };

    const handleBranchChange = (branch: SingleValue<EmployeeBranch>) => {
        if (!branch) {
            return;
        }

        setEditedEmployeeInformation(prevState =>
            prevState ? { ...prevState, branch: branch.BRANCHCODE } : undefined
        );
    };

    const handlePositionChange = (position: SingleValue<F1PositionMaster>) => {
        if (!position) {
            return;
        }

        setEditedEmployeeInformation(prevState =>
            prevState ? { ...prevState, position: position.PositionCode } : undefined
        );
    };

    const handleDeleteUserClick = async () => {
        const result = await Swal.fire({
            title: `ยืนยันลบผู้ใช้ ${debouncedEditedUserId}`,
            icon: 'warning',
            showDenyButton: true,
            denyButtonText: 'ไม่ต้องการ',
            confirmButtonText: 'ต้องการ'
        });
        if (!result.isConfirmed) {
            return;
        }

        const sid = user?.sid;
        const userId = debouncedEditedUserId;
        const systemType = user?.system_type;

        if (!sid || !userId || !systemType) {
            return;
        }

        deleteUser(
            { sid, userId, systemType },
            {
                onSuccess: () => {
                    const _ = Swal.fire({
                        title: 'ลบผู้ใช้สำเร็จ',
                        icon: 'success',
                        confirmButtonText: 'Yes'
                    });

                    refetchCanUseUserId();
                    refetchExistingEmployeeInformationByEmployeeCode();
                    refetchExistingEmployeeInformationByUserId();
                },
                onError: () => {
                    const _ = Swal.fire({
                        title: 'ไม่สามารถลบผู้ใช้ได้ กรุณาติดต่อผู้ดูแล',
                        icon: 'error',
                        confirmButtonText: 'Yes'
                    });
                }
            }
        );
    };

    const handleConfirmApproveClick = async () => {
        if (!user || !editedEmployeeInformation) return;

        // Validate required fields
        const emptyFields = FIELDS.filter(field => field.required)
            .filter(field => !editedEmployeeInformation[field.name]?.trim())
            .map(field => field.name);

        setInvalidFields(emptyFields);

        if (emptyFields.length > 0) {
            await Swal.fire({
                title: 'กรุณากรอกข้อมูลให้ครบถ้วน',
                icon: 'warning'
            });

            return;
        }

        if (!editedEmployeeInformation.employee_code) {
            const result = await Swal.fire({
                title: 'รหัสพนักงานว่าง ต้องการใส่รหัสพนักงานหรือไม่?',
                icon: 'warning',
                showDenyButton: true,
                denyButtonText: 'ไม่ต้องการ',
                confirmButtonText: 'ต้องการ'
            });

            if (result.isConfirmed) return;
        }

        let preparedEmployeeInformation = {
            ...editedEmployeeInformation,
            prefix: selectedPrefix?.TitleCode ?? '',
            branch: selectedBranch?.BRANCHCODE ?? '',
            position: selectedPosition?.PositionCode ?? ''
        };

        if (existingEmployeeInformationByEmployeeCode) {
            const result = await Swal.fire({
                title: 'พบข้อมูล Employee ในระบบแล้ว',
                text: 'ต้องการอัพเดทข้อมูลหรือไม่?',
                icon: 'warning',
                showDenyButton: true,
                denyButtonText: 'ไม่ต้องการ',
                confirmButtonText: 'ต้องการ'
            });

            if (!result.isConfirmed) {
                // Use existing employee information instead of edited
                preparedEmployeeInformation = {
                    branch: existingEmployeeInformationByEmployeeCode.BranchCode,
                    department: existingEmployeeInformationByEmployeeCode.Department,
                    email: existingEmployeeInformationByEmployeeCode.Email,
                    employee_code: existingEmployeeInformationByEmployeeCode.EmployeeCode,
                    first_name_en: existingEmployeeInformationByEmployeeCode.FirstName,
                    first_name_th: existingEmployeeInformationByEmployeeCode.FirstName_TH,
                    last_name_en: existingEmployeeInformationByEmployeeCode.LastName,
                    last_name_th: existingEmployeeInformationByEmployeeCode.LastName_TH,
                    position: existingEmployeeInformationByEmployeeCode.PositionCode,
                    prefix: existingEmployeeInformationByEmployeeCode.NamePrefix,
                    user_id: editedEmployeeInformation.user_id
                };
            }
        }

        await onConfirmApprove(
            { ...user, employee_information: preparedEmployeeInformation },
            'approve',
            approveWorkflow,
            approveDms
        );

        onClose();
    };

    const selectableMap = {
        // Existing prefix
        NamePrefix: {
            isDisabled: true,
            options: selectablePrefixes,
            getOptionLabel: (option: EmployeePrefix) => option.TitleName,
            getOptionValue: (option: EmployeePrefix) => option.TitleCode,
            value: selectedExistingPrefix
        },
        prefix: {
            options: selectablePrefixes,
            getOptionLabel: (option: EmployeePrefix) => option.TitleName,
            getOptionValue: (option: EmployeePrefix) => option.TitleCode,
            value: selectedPrefix,
            onChange: handlePrefixChange
        },
        // Existing branch
        BranchCode: {
            isDisabled: true,
            options: selectableBranches,
            getOptionLabel: getBranchOptionLabel,
            getOptionValue: (option: EmployeeBranch) => option.BRANCHCODE,
            value: selectedExistingBranch
        },
        branch: {
            options: selectableBranches,
            getOptionLabel: getBranchOptionLabel,
            getOptionValue: (option: EmployeeBranch) => option.BRANCHCODE,
            value: selectedBranch,
            onChange: handleBranchChange
        },
        PositionCode: {
            isDisabled: true,
            options: selectablePositions,
            getOptionLabel: getPositionOptionLabel,
            getOptionValue: (option: F1PositionMaster) => option.PositionCode,
            value: selectedExistingPosition
        },
        position: {
            options: selectablePositions,
            getOptionLabel: getPositionOptionLabel,
            getOptionValue: (option: F1PositionMaster) => option.PositionCode,
            value: selectedPosition,
            onChange: handlePositionChange
        }
    };

    const editedFieldStatusMap = useMemo(() => {
        if (!validationResult.existingEmployeeInformation) {
            return {};
        }

        const diffFields = FIELDS.filter(field => {
            if (field.type === 'select') {
                switch (field.name) {
                    case 'prefix':
                        return selectedPrefix?.TitleCode !== selectedExistingPrefix?.TitleCode;
                    case 'branch':
                        return selectedBranch?.BRANCHCODE !== selectedExistingBranch?.BRANCHCODE;
                }
            }

            return (
                validationResult.existingEmployeeInformation?.[field.existingName] !==
                editedEmployeeInformation?.[field.name]
            );
        });

        return Object.fromEntries(
            diffFields.map(diffField => [
                diffField.name,
                editedEmployeeInformation?.[diffField.name] === '' ? 'error' : 'warning'
            ])
        );
    }, [
        validationResult.existingEmployeeInformation,
        editedEmployeeInformation,
        selectedPrefix,
        selectedExistingPrefix
    ]);

    const isFetching =
        isFetchingExistingEmployeeInformationByEmployeeCode ||
        isFetchingExistingEmployeeInformationByUserId ||
        isFetchingCanUseUserId;

    return (
        <Modal
            size="5xl"
            isOpen={isOpen}
            onClose={onClose}
            scrollBehavior="outside"
        >
            <ModalOverlay />
            <ModalContent>
                <ModalHeader>Employee Information</ModalHeader>
                <ModalCloseButton />
                <ModalBody className="flex flex-col gap-4 overflow-visible">
                    <div className="flex items-center gap-4">
                        {isFetching && (
                            <Spinner
                                thickness="2px"
                                speed="0.65s"
                                color="#e55c00"
                                size="sm"
                            />
                        )}

                        {debouncedEditedUserId && !isFetching && (
                            <div className="flex flex-1 items-center gap-2">
                                {validationResult.canApprove ? (
                                    <p className="col-span-12 rounded-md bg-success-500 px-2 py-1 text-sm text-white">
                                        User ID {debouncedEditedUserId} สามารถใช้งานได้
                                    </p>
                                ) : (
                                    <>
                                        <p className="col-span-12 rounded-md bg-danger-700 px-2 py-1 text-sm text-white">
                                            {validationResult.message}
                                        </p>
                                        {validationResult.showRemoveUser && (
                                            <Button
                                                onClick={handleDeleteUserClick}
                                                className="bg-transparent p-0 px-2 text-danger-700 underline"
                                            >
                                                {isPendingDeleteUser ? (
                                                    <Spinner
                                                        thickness="2px"
                                                        speed="0.65s"
                                                        color="#e55c00"
                                                        size="sm"
                                                    />
                                                ) : (
                                                    'ลบผู้ใช้'
                                                )}
                                            </Button>
                                        )}
                                    </>
                                )}
                            </div>
                        )}
                    </div>

                    <div className="grid grid-cols-12 gap-4 overflow-visible">
                        {user &&
                            FIELDS.map(field =>
                                field.type === 'text' ? (
                                    <div
                                        key={field.label}
                                        className={twMerge(field.className, 'space-y-1.5')}
                                    >
                                        <div className="flex">
                                            <Label>{field.label}</Label>
                                            {field.asterisk && (
                                                <span className="ml-0.5 text-sm text-red-600">
                                                    *
                                                </span>
                                            )}
                                        </div>
                                        {validationResult.existingEmployeeInformation && (
                                            <Input
                                                disabled
                                                name={field.name}
                                                value={
                                                    validationResult.existingEmployeeInformation?.[
                                                        field.existingName
                                                    ]
                                                }
                                                className="w-full rounded border border-gray-300 px-2 py-1 disabled:border-[#e6e6e6] disabled:bg-[#f2f2f2]"
                                            />
                                        )}
                                        <Input
                                            required={field.required}
                                            name={field.name}
                                            value={editedEmployeeInformation?.[field.name]}
                                            onChange={event =>
                                                handleChangeEmployeeInformation(
                                                    event.target.name,
                                                    event.target.value
                                                )
                                            }
                                            className={twMerge(
                                                'w-full rounded px-2 py-1',
                                                invalidFields.includes(field.name)
                                                    ? 'border border-red-600'
                                                    : 'border border-gray-300',
                                                editedFieldStatusMap[field.name] === 'error'
                                                    ? 'border-danger-600 bg-danger-50/50'
                                                    : editedFieldStatusMap[field.name] === 'warning'
                                                    ? 'border-amber-500 bg-amber-50/50'
                                                    : ''
                                            )}
                                        />
                                    </div>
                                ) : field.type === 'select' ? (
                                    <div
                                        key={field.label}
                                        className={twMerge(field.className, 'space-y-1.5')}
                                    >
                                        <div className="flex">
                                            <Label>{field.label}</Label>
                                            {field.asterisk && (
                                                <span className="ml-0.5 text-sm text-red-600">
                                                    *
                                                </span>
                                            )}
                                        </div>
                                        {validationResult.existingEmployeeInformation && (
                                            <Creatable {...selectableMap[field.existingName]} />
                                        )}
                                        <Creatable
                                            {...selectableMap[field.name]}
                                            classNames={{
                                                control: () =>
                                                    twMerge(
                                                        'border',
                                                        editedFieldStatusMap[field.name] === 'error'
                                                            ? 'border-danger-600 bg-danger-50/50'
                                                            : editedFieldStatusMap[field.name] ===
                                                              'warning'
                                                            ? 'border-amber-500 bg-amber-50/50'
                                                            : ''
                                                    )
                                            }}
                                        />
                                    </div>
                                ) : null
                            )}
                    </div>

                    <div className="rounded border p-3 space-y-2">
                        <p className="text-sm font-medium">Workflow</p>
                        <Checkbox
                            colorScheme="primary"
                            isChecked={approveWorkflow}
                            onChange={e => setApproveWorkflow(e.target.checked)}
                        >
                            <span className="text-sm">
                                เพิ่ม user <strong>{user?.citizen_id}</strong> เข้าระบบ Workflow
                            </span>
                        </Checkbox>
                    </div>

                    <div className="rounded border p-3 space-y-2">
                        <p className="text-sm font-medium">DMS</p>
                        <Checkbox
                            colorScheme="primary"
                            isChecked={approveDms}
                            onChange={e => setApproveDms(e.target.checked)}
                        >
                            <span className="text-sm">
                                เพิ่ม user <strong>{user?.citizen_id}</strong> เข้าระบบ DMS
                            </span>
                        </Checkbox>
                    </div>
                </ModalBody>
                <ModalFooter className="gap-4">
                    <Button
                        outline
                        size="sm"
                        className="text-gray-400"
                        onClick={onClose}
                    >
                        Cancel
                    </Button>
                    <Button
                        disabled={isFetching || !validationResult.canApprove}
                        size="sm"
                        onClick={handleConfirmApproveClick}
                        className="bg-primary-900 text-white"
                    >
                        Confirm Approve
                    </Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
}

export default memo(ConfirmApproveUserModal);
